import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
} from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { EnhancedTableToolbar } from "./EnhancedTableToolbar";
import { EnhancedTableHead, Order } from "./EnhancedTableHead";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  deleteDocuments,
  getAllDocuments,
  getDocumentDependents,
  updateDocument,
} from "@services/api";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { AttachFileOutlined } from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import { getComparator, parseDate, stableSort } from "./table-functions";
import Tooltip from "@mui/material/Tooltip";
import {
  deleteReading,
  loadAllWorkspaceReadings,
} from "@features/workspace-readings/state/workspaceReadingsSlice";
import AddToWorkspaceMenu, { OwnedWorkspace } from "./AddToWorkspaceMenu";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import type { DocumentDependent } from "@/types/models";

interface Props {
  modalVisible: string;
  setModalVisible: Dispatch<SetStateAction<string>>;
  refreshKey?: number;
  // When the parent (FilesScreen) renders its own upload affordance,
  // suppress the table's inline one so we don't show two buttons.
  hideUploadButton?: boolean;
  // Workspaces the current user owns, used by the per-row
  // "Add to workspace…" picker. Empty array disables the picker.
  ownedWorkspaces?: OwnedWorkspace[];
  // Bump from the parent to nudge a refetch (after an add-to-workspace
  // succeeds, etc). Folded into the existing refreshKey behaviour.
  onRequestRefresh?: () => void;
}

export interface DocumentView {
  documentId: string;
  documentName: string;
  documentOwnerId: string | null;
  ownerName: string;
  documentUrl: string | null;
  urlExpirationDate: string | null;
  createdAt: string;
  updatedAt: string | null;
  // Every workspace this document is linked to via a reading row.
  // Drives the chip list on each row and the "Add to workspace…"
  // picker (which filters to workspaces the user owns but the
  // document isn't already in).
  workspaces: { workspaceId: string; workspaceName: string; readingId: string }[];
}

export type TableHeader = "documentName" | "ownerName" | "updatedAt";

export default function DocumentsTable({
  setModalVisible,
  refreshKey,
  hideUploadButton,
  ownedWorkspaces = [],
  onRequestRefresh,
}: Props) {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<TableHeader>("ownerName");
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState("");
  const [documents, setDocuments] = useState<DocumentView[]>([]);

  const userState = useAppSelector((state) => state.user);
  const workspaceState = useAppSelector((state) => state.workspace);
  const dispatch = useAppDispatch();

  // Delete-with-cascade-warning state. Targets are the docs about to be
  // deleted; dependents is the flattened list of readings that the FK
  // cascade will take down with them. We pre-fetch dependents on dialog
  // open so the user sees the impact before confirming.
  const [deleteTargets, setDeleteTargets] = useState<DocumentView[]>([]);
  const [deleteDeps, setDeleteDeps] = useState<DocumentDependent[]>([]);
  const [depsLoading, setDepsLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");

  // Inline rename, mirrors UnifiedResourcesTable's editingId / editValue
  // pattern so the three admin tables share the same affordance.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);

  // Tracks the reading_id currently being unlinked. Keyed by reading
  // (not document) because a single document may have multiple
  // workspace links and only one chip is in-flight at a time.
  const [unlinkingReadingId, setUnlinkingReadingId] = useState<string | null>(
    null
  );

  // "Add to workspace…" picker state. Pinned at the table level so the
  // open menu doesn't get torn down when its row re-renders. The picker
  // accepts a list of targets; we synthesize that list from either a
  // single row (per-row icon click) or the bulk selection (toolbar).
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [addMenuTargetId, setAddMenuTargetId] = useState<string | null>(null);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [actionError, setActionError] = useState<string>("");

  const addMenuTargets = useMemo(() => {
    if (bulkAddOpen) {
      return documents
        .filter((d) => selected.includes(d.documentId))
        .map((d) => ({
          documentId: d.documentId,
          documentName: d.documentName,
          linkedWorkspaceIds: new Set(
            d.workspaces.map((w) => w.workspaceId)
          ),
        }));
    }
    if (addMenuTargetId) {
      const row = documents.find((d) => d.documentId === addMenuTargetId);
      if (!row) return [];
      return [
        {
          documentId: row.documentId,
          documentName: row.documentName,
          linkedWorkspaceIds: new Set(
            row.workspaces.map((w) => w.workspaceId)
          ),
        },
      ];
    }
    return [];
  }, [bulkAddOpen, addMenuTargetId, documents, selected]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const tableData: DocumentView[] = useMemo(() => {
    if (!search.trim()) return documents;

    const searchTerm = search.toLowerCase();
    return documents.filter(
      (item) =>
        item.documentName.toLowerCase().includes(searchTerm) ||
        item.ownerName.toLowerCase().includes(searchTerm)
    );
  }, [documents, search]);

  useEffect(() => {
    if (userState.userId) {
      getAllDocuments(workspaceState.workspace_id)
        .then((result) => {
          if (!result.success) {
            console.error("Error fetching documents:", result.error.message);
            return;
          }
          setDocuments(
            result.data.map((item) => ({
              documentId: item.document_id,
              documentName: item.document_name,
              documentOwnerId: item.document_owner_id,
              ownerName: item.owner_username || "Unknown",
              documentUrl: item.document_url,
              urlExpirationDate: item.url_expiration_date,
              createdAt: item.created_at,
              updatedAt: item.updated_at,
              workspaces: (item.workspaces ?? []).map((w) => ({
                workspaceId: w.workspace_id,
                workspaceName: w.workspace_name,
                readingId: w.reading_id,
              })),
            }))
          );
        })
        .catch(console.error);
    }
  }, [userState, workspaceState.workspace_id, refreshKey]);

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = documents.map((doc) => doc.documentId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_event: MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [
        ...selected.slice(0, selectedIndex),
        ...selected.slice(selectedIndex + 1)
      ];
    }

    setSelected(newSelected);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - documents.length) : 0;

  const openDeleteDialog = async (targets: DocumentView[]) => {
    if (targets.length === 0) return;
    setDeleteTargets(targets);
    setDeleteDeps([]);
    setConfirmText("");
    setDeleteError("");
    setDepsLoading(true);

    // Fan out the per-doc dependent lookup in parallel and flatten.
    // Failed lookups (e.g. a 403 if perms changed mid-flight) collapse to
    // an empty deps list for that doc; the user still gets a usable
    // confirm — we just can't claim "0 dependents" if we couldn't check.
    const results = await Promise.all(
      targets.map((t) => getDocumentDependents(t.documentId))
    );
    const flat = results.flatMap((r) => (r.success ? r.data : []));
    setDeleteDeps(flat);
    setDepsLoading(false);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTargets([]);
    setDeleteDeps([]);
    setConfirmText("");
    setDeleteError("");
  };

  const handleDeleteFile = (id: string) => {
    const target = documents.find((d) => d.documentId === id);
    if (!target) return;
    void openDeleteDialog([target]);
  };

  const handleDeleteFiles = () => {
    const targets = documents.filter((d) => selected.includes(d.documentId));
    void openDeleteDialog(targets);
  };

  // Per-row delete with a single target uses type-the-name confirm only
  // when there are dependents — matches the plan's "If 0 deps: simple
  // Yes/No. If N deps: type-the-name." For bulk delete the per-name
  // confirm doesn't fit; we require typing "delete" instead when there's
  // a cascade.
  const singleTarget = deleteTargets.length === 1 ? deleteTargets[0] : null;
  const expectedConfirm = singleTarget
    ? singleTarget.documentName
    : deleteDeps.length > 0
      ? "delete"
      : null;
  const confirmNeeded = expectedConfirm !== null && deleteDeps.length > 0;
  const confirmOk = !confirmNeeded || confirmText === expectedConfirm;

  const startEdit = (row: DocumentView) => {
    setEditingId(row.documentId);
    setEditValue(row.documentName);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };
  const commitEdit = async (row: DocumentView) => {
    const next = editValue.trim();
    if (!next || next === row.documentName) {
      cancelEdit();
      return;
    }
    setSavingId(row.documentId);
    const result = await updateDocument(row.documentId, next, row.documentUrl);
    setSavingId(null);
    cancelEdit();
    if (!result.success) {
      console.error("Failed to rename document:", result.error);
      return;
    }
    setDocuments((prev) =>
      prev.map((d) =>
        d.documentId === row.documentId ? { ...d, documentName: next } : d
      )
    );
  };
  const handleEditKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    row: DocumentView
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void commitEdit(row);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleUnlinkLink = async (
    row: DocumentView,
    link: { workspaceId: string; workspaceName: string; readingId: string }
  ) => {
    if (
      !window.confirm(
        `Remove "${row.documentName}" from "${link.workspaceName}"? The file stays in your library; only the reading and its annotations are deleted.`
      )
    ) {
      return;
    }
    setUnlinkingReadingId(link.readingId);
    try {
      await dispatch(deleteReading(link.readingId)).unwrap();
      setDocuments((prev) =>
        prev.map((d) =>
          d.documentId === row.documentId
            ? {
                ...d,
                workspaces: d.workspaces.filter(
                  (w) => w.readingId !== link.readingId
                ),
              }
            : d
        )
      );
    } catch (err) {
      console.error("Failed to unlink document from workspace:", err);
      setActionError("Failed to remove from workspace.");
    } finally {
      setUnlinkingReadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmOk) return;
    setDeleting(true);
    setDeleteError("");

    const ids = deleteTargets.map((t) => t.documentId);
    const result = await deleteDocuments(ids);
    if (!result.success) {
      setDeleting(false);
      setDeleteError(result.error.message);
      return;
    }

    // Optimistically prune the table state.
    setDocuments((prev) => prev.filter((d) => !ids.includes(d.documentId)));
    setSelected((prev) => prev.filter((id) => !ids.includes(id)));

    // If any deleted readings lived in the current workspace, refresh the
    // Resources tab's manage list so it reflects the cascade.
    const touchedCurrent = deleteDeps.some(
      (d) => d.workspace_id === workspaceState.workspace_id
    );
    if (touchedCurrent && workspaceState.workspace_id) {
      void dispatch(loadAllWorkspaceReadings(workspaceState.workspace_id));
    }

    setDeleting(false);
    setDeleteTargets([]);
    setDeleteDeps([]);
    setConfirmText("");
  };

  return (
    <Box sx={{ width: "100%", marginY: 4 }}>
      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError("")}
        >
          {actionError}
        </Alert>
      )}
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          handleSearch={handleSearch}
          handleDelete={handleDeleteFiles}
          onAddToWorkspaceClick={
            ownedWorkspaces.length > 0 && selected.length > 0
              ? (e) => {
                  setAddMenuAnchor(e.currentTarget);
                  setBulkAddOpen(true);
                }
              : undefined
          }
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 390 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={(_, property) => {
                const isAsc = orderBy === property && order === "asc";
                setOrder(isAsc ? "desc" : "asc");
                setOrderBy(property);
              }}
              rowCount={tableData.length}
            />
            <TableBody>
              {stableSort(tableData, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = selected.includes(row.documentId);
                  const isEditing = editingId === row.documentId;
                  const isSaving = savingId === row.documentId;
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.documentId}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={(event) =>
                            handleClick(event, row.documentId)
                          }
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      </TableCell>
                      <TableCell id={row.documentId}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, row)}
                            onBlur={() => commitEdit(row)}
                            autoFocus
                            disabled={isSaving}
                            inputProps={{ maxLength: 255 }}
                            fullWidth
                          />
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body1" fontWeight={500}>
                              {row.documentName}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{row.ownerName}</TableCell>
                      <TableCell align="right">
                        {parseDate(row.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {row.workspaces.length === 0 ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Not in any workspace
                            </Typography>
                          ) : (
                            row.workspaces.map((link) => (
                              <Chip
                                key={link.readingId}
                                label={link.workspaceName}
                                size="small"
                                onDelete={() =>
                                  handleUnlinkLink(row, link)
                                }
                                disabled={
                                  unlinkingReadingId === link.readingId
                                }
                              />
                            ))
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="Rename">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => startEdit(row)}
                                disabled={isEditing}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Add to workspace…">
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setAddMenuAnchor(e.currentTarget);
                                  setAddMenuTargetId(row.documentId);
                                }}
                                disabled={
                                  isEditing || ownedWorkspaces.length === 0
                                }
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteFile(row.documentId)
                                }
                                disabled={isEditing}
                                sx={{
                                  color: "error.main",
                                  "&.Mui-disabled": {
                                    color: "error.light",
                                    opacity: 0.5,
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {!hideUploadButton && (
            <IconButton
              onClick={() => setModalVisible("choosePdf")}
              sx={{ marginLeft: "8px" }}
            >
              <Typography>Upload New</Typography>
              <AttachFileOutlined />
            </IconButton>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={tableData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage: number) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ marginLeft: "auto" }}
          />
        </Box>
      </Paper>

      <Dialog
        open={deleteTargets.length > 0}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {singleTarget
            ? `Delete "${singleTarget.documentName}"?`
            : `Delete ${deleteTargets.length} documents?`}
        </DialogTitle>
        <DialogContent>
          {depsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {deleteDeps.length === 0 ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  No readings reference {singleTarget ? "this document" : "these documents"}. Safe to delete.
                </Typography>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {singleTarget
                      ? `This document is used by ${deleteDeps.length} reading${
                          deleteDeps.length === 1 ? "" : "s"
                        }, which will also be deleted.`
                      : `These documents are used by ${deleteDeps.length} reading${
                          deleteDeps.length === 1 ? "" : "s"
                        }, which will also be deleted.`}
                  </Alert>
                  <List
                    dense
                    sx={{
                      maxHeight: 220,
                      overflowY: "auto",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    {deleteDeps.map((d) => (
                      <ListItem key={d.reading_id}>
                        <ListItemText
                          primary={d.reading_name}
                          secondary={d.workspace_name}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {confirmNeeded && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {singleTarget
                      ? "Type the document name to confirm:"
                      : `Type "delete" to confirm:`}
                  </Typography>
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder={expectedConfirm ?? ""}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    disabled={deleting}
                  />
                </>
              )}

              {deleteError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {deleteError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={depsLoading || deleting || !confirmOk}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <AddToWorkspaceMenu
        anchorEl={addMenuAnchor}
        targets={addMenuTargets}
        ownedWorkspaces={ownedWorkspaces}
        onClose={() => {
          setAddMenuAnchor(null);
          setAddMenuTargetId(null);
          setBulkAddOpen(false);
        }}
        onAdded={() => onRequestRefresh?.()}
        onError={(msg) => setActionError(msg)}
      />
    </Box>
  );
}
